import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { GTAOPass } from "three/examples/jsm/postprocessing/GTAOPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { Loader2 } from "lucide-react";

/**
 * LiveClientPreview
 * -----------------
 * Client-side companion to `RenderPreviewSwatch`. Renders a small demo
 * mesh (three.js `TorusKnotGeometry`) using the EXACT same three.js
 * pipeline that `ClientStudioRenderer` uses for seller uploads, so
 * admins see what a browser render will look like BEFORE any real STL
 * is uploaded.
 *
 * Uses the admin's UNSAVED cfg values directly — no /config/render-preset
 * roundtrip — so slider drags are instantaneous. Debounced to 150 ms.
 *
 * Sits next to RenderPreviewSwatch on the Render presets panel:
 *   Left  — server pyrender output (real pixels the server would emit)
 *   Right — browser three.js output (real pixels the seller would emit)
 * Colour identical by construction (both read the same admin cfg).
 */
const SIZE = 220;

const hexToRgbNormalized = (h) => {
  const v = String(h || "#000000").replace("#", "");
  return {
    r: parseInt(v.slice(0, 2), 16) / 255,
    g: parseInt(v.slice(2, 4), 16) / 255,
    b: parseInt(v.slice(4, 6), 16) / 255,
  };
};

export default function LiveClientPreview({ cfg, testid = "live-client-preview" }) {
  const mountRef = useRef(null);
  const canvasRef = useRef(null);
  const timerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!cfg || !mountRef.current) return;
    clearTimeout(timerRef.current);
    // 150 ms debounce — instant enough for a slider drag but avoids spinning
    // the GPU on every mouse-move event.
    timerRef.current = setTimeout(() => {
      renderOnce();
    }, 150);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cfg?.render_object_color_hex,
    cfg?.render_bg_color_hex,
    cfg?.render_object_specular,
    cfg?.render_object_metallic,
    cfg?.render_ao_strength,
    cfg?.render_light_intensity,
    cfg?.render_frame_fill,
  ]);

  function renderOnce() {
    setLoading(true);
    setErr("");
    try {
      // Extract admin values — same derivation as `/config/render-preset`.
      const objHex = cfg.render_object_color_hex || "#C8CCD0";
      const bgHex  = cfg.render_bg_color_hex || "#0F0F10";
      const specular = Number(cfg.render_object_specular ?? 0.30);
      const metallic = Number(cfg.render_object_metallic ?? 0.05);
      const roughness = Math.max(0, Math.min(1, 1 - specular * 0.65));
      const light = Number(cfg.render_light_intensity ?? 0.75);
      const ao = Number(cfg.render_ao_strength ?? 0.90);
      const frame = Number(cfg.render_frame_fill ?? 0.85);

      let canvas = canvasRef.current;
      if (!canvas) {
        canvas = document.createElement("canvas");
        canvas.width = SIZE;
        canvas.height = SIZE;
        canvas.style.width = SIZE + "px";
        canvas.style.height = SIZE + "px";
        mountRef.current.innerHTML = "";
        mountRef.current.appendChild(canvas);
        canvasRef.current = canvas;
      }

      const renderer = new THREE.WebGLRenderer({
        canvas, antialias: true, alpha: false, preserveDrawingBuffer: true,
      });
      renderer.setSize(SIZE, SIZE, false);
      renderer.setPixelRatio(1);
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.15;
      renderer.outputColorSpace = THREE.SRGBColorSpace;

      const scene = new THREE.Scene();
      const bg = hexToRgbNormalized(bgHex);
      scene.background = new THREE.Color(bg.r, bg.g, bg.b);

      const pmrem = new THREE.PMREMGenerator(renderer);
      scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.06).texture;

      // Demo mesh — a knot has enough concavity to show off AO strength
      // without needing a real STL. Recentred + unit-scaled to reuse the
      // exact framing math ClientStudioRenderer uses.
      const geometry = new THREE.TorusKnotGeometry(0.35, 0.11, 128, 24);
      const obj = hexToRgbNormalized(objHex);
      const material = new THREE.MeshStandardMaterial({
        color: new THREE.Color(obj.r, obj.g, obj.b),
        metalness: metallic,
        roughness: roughness,
      });
      const mesh = new THREE.Mesh(geometry, material);
      scene.add(mesh);

      const keyLight = new THREE.DirectionalLight(0xffffff, 2.0 * light);
      keyLight.position.set(1.5, 2.0, 1.5);
      scene.add(keyLight);
      const fillLight = new THREE.DirectionalLight(0xffffff, 1.1 * light);
      fillLight.position.set(-1.5, 0.6, 1.2);
      scene.add(fillLight);
      const rimLight = new THREE.DirectionalLight(0xffffff, 0.8 * light);
      rimLight.position.set(0, -1.0, -2.0);
      scene.add(rimLight);
      scene.add(new THREE.AmbientLight(0xffffff, 0.55 * light));

      const camera = new THREE.OrthographicCamera(-frame, frame, frame, -frame, 0.01, 100);
      camera.position.set(1.5, 1.5, 2.4);
      camera.lookAt(0, 0, 0);

      // Try composer + GTAO for AO; fall back to plain render if the
      // GPU rejects the pass (e.g. Firefox no-GL environments).
      let composer = null;
      try {
        composer = new EffectComposer(renderer);
        composer.setSize(SIZE, SIZE);
        composer.addPass(new RenderPass(scene, camera));
        const gtao = new GTAOPass(scene, camera, SIZE, SIZE);
        gtao.aoRadius = 0.15;
        gtao.distanceExponent = 1.5;
        gtao.thickness = 0.05;
        gtao.scale = ao;
        composer.addPass(gtao);
        composer.addPass(new OutputPass());
      } catch {
        composer = null;
      }

      if (composer) composer.render();
      else renderer.render(scene, camera);

      // Cleanup — release GPU objects; the canvas element stays for next tick.
      geometry.dispose();
      material.dispose();
      pmrem.dispose();
      if (composer) composer.dispose?.();
      renderer.dispose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[LiveClientPreview] render failed:", e);
      setErr("Browser preview unavailable — WebGL init failed.");
    } finally {
      setLoading(false);
    }
  }

  const objectHex = (cfg?.render_object_color_hex || "#C8CCD0").toUpperCase();
  const bgHex = (cfg?.render_bg_color_hex || "#0F0F10").toUpperCase();

  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div
        ref={mountRef}
        className="rounded-md border border-border overflow-hidden relative"
        style={{ width: SIZE, height: SIZE, background: bgHex }}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[1px] pointer-events-none">
            <Loader2 className="h-5 w-5 animate-spin text-slate-700" />
          </div>
        )}
      </div>
      <div className="text-xs font-mono space-y-1">
        <div>Object <b>{objectHex}</b></div>
        <div>Background <b>{bgHex}</b></div>
        {err ? (
          <div className="text-[10px] text-amber-600 uppercase tracking-widest mt-2">{err}</div>
        ) : (
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest mt-2">
            Browser · Same pipeline as seller renders
          </div>
        )}
      </div>
    </div>
  );
}
